import * as React from "react";
import { useState } from "react";
import { useNotify } from "react-admin";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ApiClient from "../../ApiClient";
import API from "../../Routes";

const ATS_PROVIDERS = ["GREENHOUSE", "RECRUITEE", "TEAM_TAILOR"];

export const ATS_PROVIDER_LABELS = {
  GREENHOUSE: "Greenhouse",
  RECRUITEE: "Recruitee",
  TEAM_TAILOR: "TeamTailor",
};

export const parseAtsTokens = (response) => {
  const payload = response?.json ?? (response?.body ? JSON.parse(response.body) : []);
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.content)) {
    return payload.content;
  }
  return [];
};

export const fetchAtsTokens = async (organisationId) => {
  const response = await ApiClient.fetch(API.ATS.LIST_TOKENS(organisationId));
  return parseAtsTokens(response);
};

const OrganisationAtsTab = ({ organisationId, existingTokens = [], onTokensChange }) => {
  const notify = useNotify();
  const [provider, setProvider] = useState("");
  const [token, setToken] = useState("");
  const [createdToken, setCreatedToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isTeamTailor = provider === "TEAM_TAILOR";
  const hasExistingAts = existingTokens.length > 0;
  const canSubmit =
    Boolean(organisationId) &&
    Boolean(provider) &&
    !submitting &&
    !hasExistingAts &&
    (!isTeamTailor || token.trim().length > 0);

  const handleProviderChange = (event) => {
    const nextProvider = event.target.value;
    setProvider(nextProvider);
    setCreatedToken(null);

    // Greenhouse/Recruitee generate the token on the backend, so clear any leftover
    // TeamTailor value when switching away. TeamTailor keeps the input because the
    // user must paste their own token.
    if (nextProvider !== "TEAM_TAILOR") {
      setToken("");
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);
    setCreatedToken(null);

    try {
      const body = {
        organisationId,
        provider,
      };

      if (isTeamTailor) {
        body.token = token.trim();
      }

      const response = await ApiClient.fetch(API.ATS.CREATE_TOKEN, {
        method: "POST",
        body,
      });
      const created = response.json ?? JSON.parse(response.body);
      const createdTokenValue = created?.token;

      try {
        const tokens = await fetchAtsTokens(organisationId);
        onTokensChange?.(tokens);
      } catch (error) {
        if (createdTokenValue) {
          onTokensChange?.([
            ...existingTokens,
            {
              organisationId,
              provider,
              token: createdTokenValue,
            },
          ]);
        }
      }

      if (isTeamTailor) {
        setToken("");
        notify("ATS token saved.");
        return;
      }

      setCreatedToken(createdTokenValue);
    } catch (error) {
      notify(
        `Failed to create ATS token: ${error?.body?.message || error?.message || error}`,
        { type: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!organisationId) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "block",
        width: "100%",
        maxWidth: 640,
        mt: 1,
      }}
    >
      {!hasExistingAts && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="ats-provider-label">Provider</InputLabel>
            <Select
              labelId="ats-provider-label"
              label="Provider"
              value={provider}
              onChange={handleProviderChange}
            >
              {ATS_PROVIDERS.map((atsProvider) => (
                <MenuItem key={atsProvider} value={atsProvider}>
                  {atsProvider}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isTeamTailor && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Token"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              required
              helperText="Required for TEAM_TAILOR"
            />
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{ mb: 4 }}
          >
            Create ATS token
          </Button>
        </>
      )}
      <Dialog
        open={Boolean(createdToken)}
        onClose={() => setCreatedToken(null)}
      >
        <DialogTitle>ATS token created</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Copy this generated token.
          </DialogContentText>
          <Typography variant="body2" sx={{ mt: 2, wordBreak: "break-all" }}>
            {createdToken}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedToken(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationAtsTab;
